
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { useOneSignal } from "./hooks/useOneSignal";
import { lazy as lazyFooter, Suspense as FooterSuspense } from "react";
const Footer = lazyFooter(() => import("./components/Footer"));

// Auth/onboarding routes should feel focused and app-native — no global chrome
const AUTH_HEADER_HIDE = new Set([
  "/signin", "/signup", "/auth", "/reset-password",
  "/verify-email", "/onboarding",
]);
const isAuthRoute = (pathname: string) => AUTH_HEADER_HIDE.has(pathname);

// Hide marketing-style footer on immersive in-app detail pages so the mobile app feels native
const ConditionalFooter = () => {
  const { pathname } = useLocation();
  const hide =
    isAuthRoute(pathname) ||
    /^\/(artists|tours|concerts|reviews|venues)\/[^/]+/.test(pathname) ||
    pathname === '/discover' ||
    pathname.startsWith('/discover/') ||
    pathname.startsWith('/connections') ||
    pathname.startsWith('/followers') ||
    pathname.startsWith('/following') ||
    pathname.startsWith('/search');
  if (hide) return null;
  return (
    <FooterSuspense fallback={null}>
      <Footer />
    </FooterSuspense>
  );
};

const ConditionalHeader = ({ onOpenShare }: { onOpenShare: () => void }) => {
  const { pathname } = useLocation();
  if (isAuthRoute(pathname)) return null;
  // Likes pages render their own contextual back nav — hide global header
  if (/\/likes$/.test(pathname) || pathname === "/likes" || pathname === "/user-likes") return null;
  return <Header onOpenShare={onOpenShare} />;
};

const ConditionalBottomNavBar = () => {
  const { pathname } = useLocation();
  if (isAuthRoute(pathname)) return null;
  return <BottomNavBar />;
};
import BottomNavBar from "./components/BottomNavBar";
import { Suspense, useState } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import Header from "./components/Header";
import { AuthProvider } from "./hooks/useAuth";
import NavigationTracker from "./components/NavigationTracker";
import ErrorBoundary from "@/components/ui/error-boundary";
import PushNotifications from "./components/PushNotifications";
import { LISTS_CANONICAL_PATH } from "@/lib/routes";



// Lazy load pages to improve performance
const Concerts = lazyWithRetry(() => import("./pages/Concerts"));
const Artists = lazyWithRetry(() => import("./pages/Artists"));
const Wishlist = lazyWithRetry(() => import("./pages/Wishlist"));
const Activity = lazyWithRetry(() => import("./pages/Activity"));
const ArtistDetail = lazyWithRetry(() => import("./pages/ArtistDetail"));
const ConcertDetail = lazyWithRetry(() => import("./pages/ConcertDetail"));
const TourDetail = lazyWithRetry(() => import("./pages/TourDetail"));
const ArtistReviews = lazyWithRetry(() => import("./pages/ArtistReviews"));
const NewReview = lazyWithRetry(() => import("./pages/NewReview"));
const SignUp = lazyWithRetry(() => import("./pages/SignUp"));
const SignIn = lazyWithRetry(() => import("./pages/SignIn"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const SearchPage = lazyWithRetry(() => import("./pages/SearchPage"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const UserProfile = lazyWithRetry(() => import("./pages/UserProfile"));
const Connections = lazyWithRetry(() => import("./pages/Connections"));
const UserConcerts = lazyWithRetry(() => import("./pages/UserConcerts"));
const UserReviews = lazyWithRetry(() => import("./pages/UserReviews"));
const UserLikes = lazyWithRetry(() => import("./pages/UserLikes"));
const ReviewDetail = lazyWithRetry(() => import("./pages/ReviewDetail"));
const ListDetail = lazyWithRetry(() => import("./pages/ListDetail"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const UserProfileReviews = lazyWithRetry(() => import("./pages/UserProfileReviews"));
const FAQs = lazyWithRetry(() => import("./pages/FAQs"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Stats = lazyWithRetry(() => import("./pages/Stats"));
const Trending = lazyWithRetry(() => import("./pages/Trending"));
const Drafts = lazyWithRetry(() => import("./pages/Drafts"));

const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const VenueDetail = lazyWithRetry(() => import("./pages/VenueDetail"));
const About = lazyWithRetry(() => import("./pages/About"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Install = lazyWithRetry(() => import("./pages/Install"));
const AppLanding = lazyWithRetry(() => import("./pages/AppLanding"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const VerifyEmail = lazyWithRetry(() => import("./pages/VerifyEmail"));
const DiscoverSection = lazyWithRetry(() => import("./pages/DiscoverSection"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const AdminDiagnostics = lazyWithRetry(() => import("./pages/AdminDiagnostics"));
const AdminFeatured = lazyWithRetry(() => import("./pages/AdminFeatured"));
const AdminReports = lazyWithRetry(() => import("./pages/AdminReports"));
const UserMoments = lazyWithRetry(() => import("./pages/UserMoments"));

const App = () => {
  // Create a new QueryClient instance with optimized defaults
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10 * 60 * 1000, // 10 minutes - data stays fresh longer
        gcTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Don't refetch if data is still fresh
        retry: 1,
      },
    },
  }));
  
  // Global state for share dialog
  const [shareOpen, setShareOpen] = useState(false);
  useOneSignal();

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            
            <NavigationTracker />
            <ConditionalHeader onOpenShare={() => setShareOpen(true)} />
            <main>
            <ErrorBoundary>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/index" element={<Navigate to="/" replace />} />
                <Route path="/concerts" element={<Concerts />} />
                <Route path="/concerts/:slug" element={<ConcertDetail />} />
                <Route path="/tours/:slug" element={<TourDetail />} />
                <Route path="/tours/:slug/reviews" element={<TourDetail />} />
                <Route path="/venue" element={<VenueDetail />} />
                <Route path="/venues/:slug" element={<VenueDetail />} />
                <Route path="/artists" element={<Navigate to="/discover" replace />} />
                <Route path="/featured" element={<Navigate to="/discover" replace />} />
                <Route path="/artists/:slug" element={<ArtistDetail />} />
                <Route path="/artists/:slug/reviews" element={<ArtistReviews />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/activity" element={<Activity />} />
                <Route path="/reviews/new" element={<NewReview />} />
                <Route path="/reviews/:id" element={<ReviewDetail />} />
                <Route path="/reviews" element={<Activity />} />
                <Route path="/lists/:id" element={<ListDetail />} />
                {/* Lists live on the profile — /lists is not a separate product */}
                <Route path="/lists" element={<Navigate to={LISTS_CANONICAL_PATH} replace />} />
                <Route path="/likes" element={<UserLikes />} />
                <Route path="/connections/:username" element={<Connections />} />
                <Route path="/connections" element={<Connections />} />
                <Route path="/followers/:username" element={<Connections />} />
                <Route path="/followers" element={<Connections />} />
                <Route path="/following/:username" element={<Connections />} />
                <Route path="/following" element={<Connections />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/search" element={<Navigate to="/discover" replace />} />
                {/* Own profile page */}
                <Route path="/profile" element={
                  <Profile 
                    shareOpen={shareOpen} 
                    setShareOpen={setShareOpen} 
                  />
                } />
                {/* New route for viewing other users' profiles */}
                <Route path="/profile/:username" element={<UserProfile />} />
                <Route path="/profile/:username/reviews" element={<UserProfileReviews />} />
                <Route path="/profile/:username/likes" element={<UserLikes />} />
                {/* Legacy /user/:username route - also renders UserProfile */}
                <Route path="/user/:username" element={<UserProfile />} />
                {/* New routes */}
                <Route path="/user-concerts" element={<UserConcerts />} />
                <Route path="/user-reviews" element={<UserReviews />} />
                <Route path="/user-likes" element={<UserLikes />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/diagnostics" element={<AdminDiagnostics />} />
                <Route path="/admin/featured" element={<AdminFeatured />} />
                <Route path="/admin/reports" element={<AdminReports />} />
                <Route path="/stats" element={<Stats />} />
                <Route path="/faqs" element={<FAQs />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/trending" element={<Navigate to="/discover" replace />} />
                <Route path="/popular" element={<Navigate to="/discover?tab=feed&filter=popular" replace />} />
                <Route path="/discover" element={<SearchPage />} />
                <Route path="/discover/section/:sectionId" element={<DiscoverSection />} />
                <Route path="/profile/drafts" element={<Drafts />} />
                <Route path="/about" element={<About />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/install" element={<Install />} />
                <Route path="/app" element={<AppLanding />} />
                <Route path="/support" element={<Support />} />
                <Route path="/moments" element={<UserMoments />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
            </main>
            <PushNotifications />
            <ConditionalBottomNavBar />
            <ConditionalFooter />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
};

export default App;
